// Web implementation of stale code-split chunk recovery.
//
// After a frontend redeploy the chunk filename hashes rotate. A browser holding
// an older index.html (especially iPad / Mobile Safari resuming a backgrounded
// tab) requests a chunk hash that no longer exists. Cloudflare Pages serves the
// SPA index.html (200) for that missing asset, so the dynamic import either
// rejects ("Loading chunk N failed") or throws a SyntaxError parsing HTML as JS
// ("Unexpected token '<'"). Either way the lazily loaded view crashes.
//
// The fix is to reload once so the browser fetches a fresh index.html (made
// no-cache in WS1 / DHT-4109) that references the current chunk hashes. A
// timestamped guard in sessionStorage prevents a reload loop if the failure
// somehow persists. The same guard key is shared with the webpack
// RetryChunkLoadPlugin lastResortScript so the two mechanisms never double-fire.
//
// DHT-5796 adds two pieces of per-document state on top of that guard:
//
// - "Reload pending": set just before either path calls location.reload(), so
//   the root error boundary can show a spinner (the page is about to go away)
//   rather than a crash screen. Time-boxed to RELOAD_PENDING_MS: if the document
//   is still alive after that (the user chose "Stay" on a beforeunload prompt),
//   the boundary falls through to its Retry screen. Deliberately NOT derived
//   from the sessionStorage guard, which survives into the next document and
//   would read a fresh failure there as "pending".
// - "User retry in flight": a user pressing Retry re-imports the failed chunk in
//   place (retryableLazy). While that load is in flight neither automatic path
//   reloads, whatever the guard says: the webpack plugin's retry counter is
//   never reset, so a re-import after it gave up gets one attempt and then runs
//   lastResortScript at once, which would otherwise reload under the user.
//
// Two guards coexist on purpose and are not merged: this one (time-windowed,
// shared with lastResortScript) and AutumnErrorBoundary's permanent
// `chunk-load-reload` flag for in-app route chunks.

const RELOAD_TS_KEY = "dapple-chunk-reload-ts";
const RELOAD_WINDOW_MS = 30_000;

/** How long after a chunk reload is requested the boundary treats it as pending. */
export const RELOAD_PENDING_MS = 10_000;

type ChunkReloadWindow = Window & {
  /** ms epoch, set by lastResortScript just before it reloads. */
  __dappleChunkReloadAt?: number;
  /** true while a user-initiated chunk retry is in flight. */
  __dappleUserChunkRetry?: boolean;
};

// Set by reloadOnceForChunkError on its reload branches. Module state is
// per-document, which is exactly the lifetime "pending" needs.
let reloadRequestedAt: number | null = null;
let userRetriesInFlight = 0;

const chunkWindow = (): ChunkReloadWindow | undefined =>
  typeof window === "undefined" ? undefined : (window as ChunkReloadWindow);

const markReloadRequested = () => {
  reloadRequestedAt = Date.now();
};

/**
 * How much longer the current chunk reload counts as pending, in ms: 0 when
 * this document has not asked for one in the last RELOAD_PENDING_MS. Either
 * reload path counts.
 */
export function chunkReloadPendingRemainingMs(): number {
  const fromWebpack = chunkWindow()?.__dappleChunkReloadAt;
  const latest = Math.max(
    reloadRequestedAt ?? 0,
    typeof fromWebpack === "number" ? fromWebpack : 0,
  );
  if (latest <= 0) return 0;
  return Math.max(0, RELOAD_PENDING_MS - (Date.now() - latest));
}

/**
 * True iff this document asked for a chunk reload less than RELOAD_PENDING_MS
 * ago, from either reload path.
 */
export function isChunkReloadPending(): boolean {
  return chunkReloadPendingRemainingMs() > 0;
}

/**
 * Marks a user-initiated chunk retry as in flight. Returns the function that
 * ends it; call it once the retried load settles, either way.
 */
export function beginUserChunkRetry(): () => void {
  userRetriesInFlight += 1;
  const w = chunkWindow();
  if (w) w.__dappleUserChunkRetry = true;
  let ended = false;
  return () => {
    if (ended) return;
    ended = true;
    userRetriesInFlight = Math.max(0, userRetriesInFlight - 1);
    const current = chunkWindow();
    if (current && userRetriesInFlight === 0) {
      current.__dappleUserChunkRetry = false;
    }
  };
}

export function isUserChunkRetryInFlight(): boolean {
  return userRetriesInFlight > 0;
}

const CHUNK_ERROR_PATTERN =
  /ChunkLoadError|Loading chunk\b|Loading CSS chunk|error loading dynamically imported module|Importing a module script failed|module script failed|Unexpected token '<'|expected expression, got '<'/i;

export function isChunkError(error: unknown): boolean {
  if (!error) return false;
  if (typeof error === "string") return CHUNK_ERROR_PATTERN.test(error);
  const e = error as { name?: string; message?: string };
  return CHUNK_ERROR_PATTERN.test(`${e.name ?? ""} ${e.message ?? ""}`);
}

// Reloads the page at most once per RELOAD_WINDOW_MS. Returns true if a reload
// was triggered. Safe to call from multiple places (error boundary, global
// listeners, webpack lastResortScript) — the shared timestamp coordinates them.
//
// Declines, without writing the guard, while a user retry is in flight: the
// user asked to stay on this page, and the Retry screen is the fallback.
export function reloadOnceForChunkError(): boolean {
  if (typeof window === "undefined") return false;
  if (isUserChunkRetryInFlight()) return false;
  try {
    const now = Date.now();
    const last = Number(window.sessionStorage.getItem(RELOAD_TS_KEY) ?? "0");
    if (now - last < RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOAD_TS_KEY, String(now));
    markReloadRequested();
    window.location.reload();
    return true;
  } catch {
    // sessionStorage may be unavailable (private mode / blocked storage). The
    // loop guard is then best-effort, but recovering the page still beats
    // leaving the user on a broken screen.
    try {
      markReloadRequested();
      window.location.reload();
      return true;
    } catch {
      // Nothing else we can do; report that no reload was triggered.
      return false;
    }
  }
}

// The "error" listener below sees every script on the page, including a
// clinic's own tag-manager tags. A broken one (a src pasted with curly quotes,
// answered with index.html) throws "Unexpected token '<'" exactly like a stale
// chunk, and reloading for it is not harmless: the deposit link holds its token
// in memory only once it is stripped from the URL (DHT-5734), so the reload
// lost it and the patient got a blank page. Fail open: only a script served
// from our own bundle path counts. An error with no source file keeps the old
// behaviour, so stale-chunk recovery does not regress.
function isOwnBundleScript(filename: string | undefined): boolean {
  if (!filename) return true;
  try {
    const url = new URL(filename, window.location.href);
    return (
      url.origin === window.location.origin &&
      url.pathname.startsWith("/static/")
    );
  } catch {
    return false;
  }
}

// Global backstop for chunk failures that are not caught by a React error
// boundary — e.g. the top-level dynamic import in the app entry, or a rejected
// import() in code outside the forms flow.
export function installChunkErrorReloadHandler(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("error", event => {
    if (!isOwnBundleScript(event.filename)) return;
    if (isChunkError(event.error) || isChunkError(event.message)) {
      reloadOnceForChunkError();
    }
  });
  window.addEventListener("unhandledrejection", event => {
    if (isChunkError(event.reason)) {
      reloadOnceForChunkError();
    }
  });
}
